/**
 * Vue projet — règles PURES (IA v4 § 4.2, § 4.2.1, § 4.2.3 ; ARB-r2-07, ARB-r2-09 ;
 * planches `ProduitApercuProjet`, `VueProjetEtats`). Aucune de ces fonctions n'appelle la
 * passerelle : elles disent ce que les données déjà lues impliquent.
 *
 *  - Santé v1.1 : 7 signaux, la couleur du projet est le PIRE signal disponible, jamais une
 *    moyenne ; un signal sans donnée vaut « sans objet » (gris) et JAMAIS vert.
 *  - Jauge du cycle : 9 étapes, nombre de demandes par étape ; une demande compte à l'étape la
 *    plus avancée atteinte par un de ses items (ARB-r2-09).
 */
import { lireInstant } from './horodatage'

export const FORMULE_SANTE = 'v1.1'

export type Couleur = 'vert' | 'ambre' | 'rouge' | 'sans-objet'
export type CleSignal = 'qualite' | 'livraison' | 'decisions' | 'conformite' | 'colonie' | 'exploitation' | 'generabilite'

export interface Signal {
  cle: CleSignal
  libelle: string
  couleur: Couleur
  /** Ce qui détermine la couleur, en une phrase (« 1 anomalie bloquante depuis 9 j »). */
  raison: string
  source: string
}

export interface Sante {
  couleur: Couleur
  signaux: Signal[]
  /** Les signaux qui donnent la couleur du projet (vide si « sans objet »). */
  determinants: Signal[]
  /** « Rouge : 2 signaux — qualité : … ; conformité : … ». */
  phrase: string
}

const JOUR_MS = 86_400_000
const HEURE_MS = 3_600_000
const RANG: Record<Couleur, number> = { 'sans-objet': -1, vert: 0, ambre: 1, rouge: 2 }
export const LIBELLE_COULEUR: Record<Couleur, string> = { vert: 'vert', ambre: 'ambre', rouge: 'rouge', 'sans-objet': 'sans objet' }

/** Anomalie « ouverte » pour la santé : `resolved` compte (ARB-r2-07, à vérifier = ouverte). */
const ETATS_OUVERTS = new Set(['new', 'triaged', 'in_progress', 'reopened', 'resolved'])
/** « Bloquante ou critique » : les deux plus hautes sévérités du modèle (`blocker`, `major`). */
const SEVERITES_GRAVES = new Set(['blocker', 'major'])

export interface EntreesSante {
  maintenant: Date
  anomalies?: readonly { severity: string; state: string; created_at: string }[] | undefined
  /** Régression non acceptée à la dernière non-régression (non exposé : laissé indéfini). */
  regressionNonAcceptee?: boolean | undefined
  deploiements?: readonly {
    status: string; rank: number; environment?: string; operation_type?: string | null | undefined
    started_at?: string | undefined; finished_at?: string | null | undefined
  }[] | undefined
  approbationsEnAttente?: readonly { created_at: string }[] | undefined
  derogations?: readonly { effective_status: string; requirement_code?: string; expires_at?: string }[] | undefined
  missions?: readonly { title?: string; status: string; stop_reason: string | null; updated_at: string }[] | undefined
  /** Tâches de colonie arrêtées sans décision (Boîte d'attention `mission_task_stopped`). */
  tachesArretees?: readonly { what?: string | null; created_at: string }[] | undefined
  budgetsErreur?: readonly { slo_key?: string; budget_total: number; budget_consumed: number }[] | undefined
  /** Profil de pile du projet ; `null` = aucun profil décidé ; `undefined` = non lu. */
  pile?: { proposable: boolean; libelle: string; livraisonManuelle?: boolean } | null | undefined
}

/** Âge d'une date de la passerelle (AUD-CRAWL-K1 : `… +00` lu, plus jamais 0) ; illisible : 0. */
const age = (iso: string | null | undefined, maintenant: Date): number => {
  const t = lireInstant(iso)
  return t == null ? 0 : Math.max(0, maintenant.getTime() - t)
}

/** « 9 j », « 3 h », « 25 min ». */
export function duree(ms: number): string {
  if (ms >= 2 * JOUR_MS) return `${Math.floor(ms / JOUR_MS)} j`
  if (ms >= HEURE_MS) return `${Math.floor(ms / HEURE_MS)} h`
  return `${Math.max(0, Math.floor(ms / 60_000))} min`
}

const reussi = (s: string): boolean => /succe|success|deployed|done|ok/i.test(s)
const echoue = (s: string): boolean => /fail|error|echec/i.test(s)

function signal(cle: CleSignal, libelle: string, source: string, couleur: Couleur, raison: string): Signal {
  return { cle, libelle, source, couleur, raison }
}

export function signalQualite(e: EntreesSante): Signal {
  const s = (c: Couleur, r: string): Signal => signal('qualite', 'Qualité', 'anomalies · rapport de non-régression', c, r)
  if (!e.anomalies) return s('sans-objet', 'anomalies non lues')
  const ouvertes = e.anomalies.filter((a) => ETATS_OUVERTS.has(a.state) && SEVERITES_GRAVES.has(a.severity))
  const vieilles = ouvertes.filter((a) => age(a.created_at, e.maintenant) >= 7 * JOUR_MS)
  if (e.regressionNonAcceptee) return s('rouge', 'régression non acceptée à la dernière non-régression')
  if (vieilles.length > 0) {
    const plus = Math.max(...vieilles.map((a) => age(a.created_at, e.maintenant)))
    return s('rouge', `${vieilles.length} anomalie(s) bloquante(s) ou critique(s) ouverte(s) depuis ${duree(plus)}`)
  }
  if (ouvertes.length > 0) return s('ambre', `${ouvertes.length} anomalie(s) bloquante(s) ou critique(s) ouverte(s) depuis moins de 7 j`)
  return s('vert', 'aucune anomalie bloquante ou critique ouverte')
}

export function signalLivraison(e: EntreesSante): Signal {
  const s = (c: Couleur, r: string): Signal => signal('livraison', 'Livraison', 'deployments', c, r)
  const d = e.deploiements
  if (!d || d.length === 0) return s('sans-objet', 'aucun déploiement')
  const instant = (x: (typeof d)[number]): number => -age(x.finished_at ?? x.started_at, e.maintenant)
  // Rouge : sur un environnement, le DERNIER déploiement a échoué (rien ne l'a rétabli depuis).
  const parEnv = new Map<string, (typeof d)[number]>()
  for (const x of d) {
    const cle = x.environment ?? String(x.rank)
    const connu = parEnv.get(cle)
    if (!connu || instant(x) > instant(connu)) parEnv.set(cle, x)
  }
  const enEchec = [...parEnv.values()].filter((x) => echoue(x.status))
  if (enEchec.length > 0) return s('rouge', `déploiement en échec non rétabli (${enEchec.map((x) => x.environment ?? `palier ${x.rank}`).join(', ')})`)
  if (d.some((x) => x.operation_type === 'rollback' && age(x.finished_at ?? x.started_at, e.maintenant) < 7 * JOUR_MS)) {
    return s('ambre', 'retour arrière dans les 7 derniers jours')
  }
  const haut = Math.max(...d.map((x) => x.rank))
  const dernierHaut = [...d].filter((x) => x.rank === haut).sort((a, b) => instant(b) - instant(a))[0]!
  return reussi(dernierHaut.status)
    ? s('vert', 'dernier déploiement du palier le plus haut réussi')
    : s('sans-objet', 'dernier déploiement du palier le plus haut en cours')
}

export function signalDecisions(e: EntreesSante): Signal {
  const s = (c: Couleur, r: string): Signal => signal('decisions', 'Décisions', 'approbations en attente', c, r)
  if (!e.approbationsEnAttente) return s('sans-objet', 'approbations non lues')
  if (e.approbationsEnAttente.length === 0) return s('vert', 'aucune approbation en attente')
  const plus = Math.max(...e.approbationsEnAttente.map((a) => age(a.created_at, e.maintenant)))
  const r = `plus ancienne approbation en attente : ${duree(plus)} (cible 48 h)`
  if (plus > 5 * JOUR_MS) return s('rouge', r)
  if (plus >= 48 * HEURE_MS) return s('ambre', r)
  return s('vert', r)
}

export function signalConformite(e: EntreesSante): Signal {
  const s = (c: Couleur, r: string): Signal => signal('conformite', 'Conformité', 'exigences héritées · dérogations', c, r)
  if (!e.derogations) return s('sans-objet', 'dérogations non lues')
  const echues = e.derogations.filter((w) => w.effective_status === 'expired')
  if (echues.length > 0) {
    const codes = echues.map((w) => w.requirement_code).filter(Boolean).join(', ')
    return s('rouge', `dérogation échue${codes ? ` ${codes}` : ''}`)
  }
  return s('vert', 'tout conforme ou sous dérogation valide')
}

export function signalColonie(e: EntreesSante): Signal {
  const s = (c: Couleur, r: string): Signal => signal('colonie', 'Colonie', 'missions', c, r)
  if (!e.missions) return s('sans-objet', 'missions non lues')
  const enEchec = e.missions.filter((m) => (m.status === 'failed' || m.stop_reason === 'budget')
    && age(m.updated_at, e.maintenant) > 72 * HEURE_MS)
  if (enEchec.length > 0) return s('rouge', `mission en échec ou budget épuisé sans décision depuis plus de 72 h (${enEchec.map((m) => m.title ?? '').join(', ')})`)
  const arretees = (e.tachesArretees ?? []).map((t) => ({ t, a: age(t.created_at, e.maintenant) }))
  const longues = arretees.filter((x) => x.a > 24 * HEURE_MS)
  if (longues.length > 0) return s('ambre', `tâche arrêtée sans décision depuis ${duree(Math.max(...longues.map((x) => x.a)))}`)
  if (arretees.length > 0) {
    return s('vert', `tâche arrêtée depuis ${duree(Math.max(...arretees.map((x) => x.a)))} : ambre seulement après 24 h`)
  }
  return s('vert', 'aucune tâche arrêtée sans décision')
}

export function signalExploitation(e: EntreesSante): Signal {
  const s = (c: Couleur, r: string): Signal => signal('exploitation', 'Exploitation', '/kpi/error-budget', c, r)
  const b = (e.budgetsErreur ?? []).filter((x) => x.budget_total > 0)
  if (b.length === 0) return s('sans-objet', 'aucun budget d’erreur défini')
  const pire = Math.max(...b.map((x) => x.budget_consumed / x.budget_total))
  const r = `budget d’erreur consommé à ${Math.round(pire * 100)} %`
  if (pire >= 1) return s('rouge', r)
  if (pire >= 0.8) return s('ambre', r)
  return s('vert', r)
}

export function signalGenerabilite(e: EntreesSante): Signal {
  const s = (c: Couleur, r: string): Signal => signal('generabilite', 'Générabilité', 'profil de pile', c, r)
  if (e.pile === undefined) return s('sans-objet', 'profil de pile non lu')
  if (e.pile === null) return s('sans-objet', 'aucune pile décidée (ADR)')
  if (e.pile.livraisonManuelle) return s('sans-objet', 'livraison manuelle assumée')
  return e.pile.proposable
    ? s('vert', `profil ${e.pile.libelle} proposable`)
    : s('ambre', `profil ${e.pile.libelle} non générable, sans livraison manuelle assumée`)
}

/** Phrase de la santé : couleur du projet et ses signaux déterminants, nommés. */
export function composerSante(couleur: Couleur, signaux: Signal[]): Sante {
  const determinants = couleur === 'sans-objet' ? [] : signaux.filter((x) => x.couleur === couleur)
  const titre = LIBELLE_COULEUR[couleur].charAt(0).toUpperCase() + LIBELLE_COULEUR[couleur].slice(1)
  const phrase = couleur === 'sans-objet'
    ? 'Sans objet : aucun signal n’a de donnée.'
    : couleur === 'vert'
      ? 'Vert : tous les signaux disponibles sont verts.'
      : `${titre} : ${determinants.length} ${determinants.length > 1 ? 'signaux' : 'signal'} — ${determinants
        .map((x) => `${x.libelle.toLowerCase()} : ${x.raison}`).join(' ; ')}.`
  return { couleur, signaux, determinants, phrase }
}

/** Santé v1.1 : pire signal DISPONIBLE ; tous « sans objet » ⇒ « sans objet ». */
export function calculerSante(e: EntreesSante): Sante {
  const signaux = [signalQualite(e), signalLivraison(e), signalDecisions(e), signalConformite(e),
    signalColonie(e), signalExploitation(e), signalGenerabilite(e)]
  const pire = signaux.reduce<Couleur>((acc, x) => (RANG[x.couleur] > RANG[acc] ? x.couleur : acc), 'sans-objet')
  return composerSante(pire, signaux)
}

// ---------------------------------------------------------------------------------------------
// Jauge du cycle (SRF-PRJ-026, ARB-r2-09)
// ---------------------------------------------------------------------------------------------

export const ETAPES = [
  { n: 1, libelle: 'Cadrage' },
  { n: 2, libelle: 'Choix technologiques' },
  { n: 3, libelle: 'Design' },
  { n: 4, libelle: 'Spécification' },
  { n: 5, libelle: 'Planification' },
  { n: 6, libelle: 'Développement par les agents' },
  { n: 7, libelle: 'Tests et qualité' },
  { n: 8, libelle: 'Livraison' },
  { n: 9, libelle: 'Exploitation' },
] as const
export const PASTILLES = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨'] as const

/** `rouverte` : un item de l'étape avait été terminé puis est revenu (calcul serveur seulement). */
export type EtatEtape = 'à venir' | 'en cours' | 'rouverte' | 'faite' | 'non activée'

export interface EtapeJauge { n: number; libelle: string; demandes: number; etat: EtatEtape }
export interface Jauge { etapes: EtapeJauge[]; total: number; phrase: string }

/** Une demande et l'étape atteinte par chacun de ses items (la demande elle-même comprise). */
export interface DemandeEtapes { id: string | number; etapesItems: readonly number[] }

/** Étape d'une demande = la PLUS AVANCÉE atteinte par au moins un de ses items (ARB-r2-09). */
export function etapeDemande(d: DemandeEtapes): number {
  return d.etapesItems.length === 0 ? 1 : Math.max(...d.etapesItems)
}

/**
 * Jauge : nombre de demandes par étape. État : `non activée` (absente du gabarit, jamais
 * comptée), `en cours` (≥ 1 demande), `faite` (aucune demande et au moins une demande l'a
 * franchie par TOUS ses items), `à venir` sinon. « Rouverte » demande l'historique des
 * étapes : non calculable côté client (écart consigné).
 */
export function calculerJauge(demandes: readonly DemandeEtapes[], activees?: ReadonlySet<number>): Jauge {
  const compte = new Map<number, number>()
  for (const d of demandes) {
    let k = etapeDemande(d)
    // Une étape non activée n'est jamais comptée : la demande compte à l'étape activée précédente.
    while (activees && k > 1 && !activees.has(k)) k -= 1
    compte.set(k, (compte.get(k) ?? 0) + 1)
  }
  const etapes: EtapeJauge[] = ETAPES.map(({ n, libelle }) => {
    if (activees && !activees.has(n)) return { n, libelle, demandes: 0, etat: 'non activée' }
    const nb = compte.get(n) ?? 0
    const franchie = demandes.some((d) => d.etapesItems.length > 0 && Math.min(...d.etapesItems) > n)
    return { n, libelle, demandes: nb, etat: nb > 0 ? 'en cours' : franchie ? 'faite' : 'à venir' }
  })
  return { etapes, total: demandes.length, phrase: phraseJauge(etapes, demandes.length) }
}

/** « En cours : ⑤ Planification (2 demandes), ⑥ … (1) ; non activée : … ». */
export function phraseJauge(etapes: readonly EtapeJauge[], total: number): string {
  const enCours = etapes.filter((e) => e.etat === 'en cours' || e.etat === 'rouverte')
  const nonActivees = etapes.filter((e) => e.etat === 'non activée')
  let phrase = total === 0
    ? 'Aucune demande : toutes les étapes sont à venir.'
    : `En cours : ${enCours.map((e, i) => `${PASTILLES[e.n - 1]} ${e.libelle} (${e.demandes}${i === 0 ? ` demande${e.demandes > 1 ? 's' : ''}` : ''})${e.etat === 'rouverte' ? ' rouverte' : ''}`).join(', ')}.`
  if (nonActivees.length > 0) {
    phrase = `${phrase.replace(/\.$/, '')} ; non activée : ${nonActivees.map((e) => `${PASTILLES[e.n - 1]} ${e.libelle}`).join(', ')}.`
  }
  return phrase
}

/** Item du backlog, réduit à ce qui situe son étape. */
export interface ItemEtape {
  id: number; kind: string; parent_id: number | null; status: string; sprint_id: number | null
  version_name?: string | null
}

/**
 * Étape atteinte par un item, déduite du backlog seul (approximation client, écart consigné :
 * la filiation demande → items n'a pas d'agrégat serveur). ② et ③ n'ont pas d'item propre.
 */
export function etapeItem(item: ItemEtape, versionsEnProduction: ReadonlySet<string> = new Set()): number {
  if (item.kind === 'idea') return 1
  if (item.kind === 'cadrage') return item.status === 'done' ? 4 : 1
  if (item.status === 'done') return item.version_name && versionsEnProduction.has(item.version_name) ? 9 : 8
  if (item.status === 'in_review') return 7
  if (item.status === 'in_progress') return 6
  return 5
}

/** Demandes = items `idea` ; leurs items = leurs descendants par `parent_id`. */
export function demandesDepuisBacklog(items: readonly ItemEtape[], versionsEnProduction: ReadonlySet<string> = new Set()): DemandeEtapes[] {
  const enfants = new Map<number, ItemEtape[]>()
  for (const i of items) {
    if (i.parent_id == null) continue
    enfants.set(i.parent_id, [...(enfants.get(i.parent_id) ?? []), i])
  }
  return items.filter((i) => i.kind === 'idea').map((idee) => {
    const etapes: number[] = [etapeItem(idee, versionsEnProduction)]
    const pile = [...(enfants.get(idee.id) ?? [])]
    const vus = new Set<number>([idee.id])
    while (pile.length > 0) {
      const x = pile.pop()!
      if (vus.has(x.id)) continue
      vus.add(x.id)
      etapes.push(etapeItem(x, versionsEnProduction))
      pile.push(...(enfants.get(x.id) ?? []))
    }
    // La demande elle-même ne retient pas l'étape ① dès qu'un item l'a dépassée : seuls ses
    // items comptent pour « faite » (ARB-r2-09 : faite quand TOUS ses items l'ont franchie).
    return { id: idee.id, etapesItems: etapes.length > 1 ? etapes.slice(1) : etapes }
  })
}

// ---------------------------------------------------------------------------------------------
// Agrégats serveur (`GET /workspaces/{id}/health` et `/lifecycle`) — la source quand elle répond
// ---------------------------------------------------------------------------------------------

/** Formes minimales lues ici (types complets : `@/api/agregatsApi`). */
export interface SanteServeurLue {
  formula_version: string
  color: string
  signals: readonly { key: string; label: string; color: string; code: string; message: string }[]
}
export interface CycleServeurLu {
  steps: readonly { step: number; label?: string; state: string; demands: number }[]
  demands_total: number
}

const COULEUR_SERVEUR: Record<string, Couleur> = { green: 'vert', amber: 'ambre', red: 'rouge', not_applicable: 'sans-objet' }
const CLE_SERVEUR: Record<string, CleSignal> = {
  quality: 'qualite', delivery: 'livraison', decisions: 'decisions', compliance: 'conformite',
  colony: 'colonie', operations: 'exploitation', generability: 'generabilite',
}
const ETAT_SERVEUR: Record<string, EtatEtape> = {
  a_venir: 'à venir', en_cours: 'en cours', rouverte: 'rouverte', faite: 'faite', non_activee: 'non activée',
}

/** Couleur serveur → couleur de l'écran ; une valeur inconnue n'est JAMAIS verte. */
export function couleurServeur(c: string | null | undefined): Couleur {
  return COULEUR_SERVEUR[c ?? ''] ?? 'sans-objet'
}

/** Santé calculée par la passerelle : sa couleur fait foi, les 7 signaux gardent leur code. */
export function santeDepuisServeur(r: SanteServeurLue): Sante {
  const signaux: Signal[] = r.signals.map((s) => ({
    cle: CLE_SERVEUR[s.key] ?? (s.key as CleSignal),
    libelle: s.label,
    couleur: couleurServeur(s.color),
    raison: s.message,
    source: `passerelle · ${s.code}`,
  }))
  return composerSante(couleurServeur(r.color), signaux)
}

/** Jauge calculée par la passerelle (ARB-r2-09) ; un état inconnu se lit « à venir », jamais « faite ». */
export function jaugeDepuisServeur(r: CycleServeurLu): Jauge {
  const parEtape = new Map(r.steps.map((s) => [s.step, s]))
  const etapes: EtapeJauge[] = ETAPES.map(({ n, libelle }) => {
    const s = parEtape.get(n)
    return { n, libelle, demandes: s?.demands ?? 0, etat: s ? (ETAT_SERVEUR[s.state] ?? 'à venir') : 'à venir' }
  })
  return { etapes, total: r.demands_total, phrase: phraseJauge(etapes, r.demands_total) }
}
